import { useEffect, useMemo, useState } from 'react';
import {
  chipLabel,
  computeActivityPresence,
  slotsLabel,
  todayIsoParis,
  type ActivityOverride,
  type ActivityPresence,
  type BoardActivity,
  type DayPresence,
  type Employee,
  type PresenceSlot,
} from '@lemontaigu/staff';
import { useBoard, useTimesheet } from '@lemontaigu/staff/react';
import {
  dayRange,
  fmtShortDayLabel,
  frDayLabel,
  shiftDay,
  shiftSummary,
  weekDays,
  weekStartKey,
} from './logic.js';

// « Semaine de … » (chip-click drill-down, 2026-07-17; 3-square format
// 2026-07-21): one employee's week rendered exactly like the Présence matrix —
// the three thirds (matin/après-midi/soir) filled when declared or
// assignment-implied, a full-red cell when Absent — plus the real clock shift.
// computeDayPresence folds declarations + ★ assignments the same way the month
// matrix does, so this needs the activity list + the employee's week of
// assignments/overrides, not declarations alone. Opened by clicking a chip on
// the day or week Gantt; week nav inside the panel.

// Column lists + row mappers now live in the BoardStore adapter (ladder step 6).
//
// This view wants ONE employee's week, but the port deliberately has no
// employee filter: adding one would double every method's shape for a table
// that holds a handful of rows per day, and this panel is only ever opened from
// a board view that has already fetched the same week. The employee filter is
// therefore applied here, after the read.

interface WeekClockEntry {
  clockIn: string;
  clockOut: string | null;
}

export function EmployeeWeek({
  employee,
  color,
  anchorDay,
  activities,
  onClose,
}: {
  employee: Employee;
  color: string | undefined;
  anchorDay: string;
  /** Board activities — needed so assignment-implied presence folds into the cells (same rule as the month matrix). */
  activities: BoardActivity[];
  onClose: () => void;
}) {
  const board = useBoard();
  const timesheet = useTimesheet();
  const [weekStart, setWeekStart] = useState(() => weekStartKey(anchorDay));
  const [presences, setPresences] = useState<ActivityPresence[]>([]);
  const [absentDates, setAbsentDates] = useState<Set<string>>(new Set());
  const [overrides, setOverrides] = useState<ActivityOverride[]>([]);
  const [clockByDay, setClockByDay] = useState<Map<string, WeekClockEntry[]>>(new Map());
  const [loaded, setLoaded] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    void (async () => {
      setLoaded(false);
      setErr('');
      const end = shiftDay(weekStart, 6);
      const { fromIso } = dayRange(weekStart);
      const { toIso } = dayRange(end);
      try {
        const [p, ab, o, c] = await Promise.all([
          board.presence(weekStart, end),
          board.absences(weekStart, end),
          // Overrides are per-activity/day, not per-employee — load the week's.
          board.overrides(weekStart, end),
          // C3 TIMESHEET read — its own port. This one DOES take an employee
          // filter, unlike the board: a range of the whole team's punches is a
          // genuinely large read.
          timesheet.entriesBetween(fromIso, toIso, employee.id),
        ]);
        setPresences(p.filter((r) => r.employeeId === employee.id));
        setAbsentDates(new Set(ab.filter((r) => r.employeeId === employee.id).map((r) => r.date)));
        setOverrides(o);
        const byDay = new Map<string, WeekClockEntry[]>();
        for (const r of c) {
          const key = todayIsoParis(new Date(r.clockIn));
          byDay.set(key, [...(byDay.get(key) ?? []), { clockIn: r.clockIn, clockOut: r.clockOut }]);
        }
        setClockByDay(byDay);
        setLoaded(true);
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [employee.id, weekStart, board, timesheet]);

  const today = todayIsoParis();
  const days = useMemo(() => weekDays(weekStart), [weekStart]);
  const presenceByDay = useMemo(() => {
    const map = new Map<string, DayPresence>();
    for (const key of days) {
      const one = computeActivityPresence({
        date: key,
        activities,
        overrides: overrides.filter((x) => x.date === key),
        presences: presences.filter((x) => x.date === key),
        absentEmployeeIds: absentDates.has(key) ? [employee.id] : [],
        currentEmployeeIds: [employee.id],
      });
      const p = one.get(employee.id);
      if (p) map.set(key, p);
    }
    return map;
  }, [days, activities, overrides, presences, absentDates, employee.id]);

  return (
    <div className="empweek-backdrop" onClick={onClose}>
      <div className="empweek" onClick={(e) => e.stopPropagation()}>
        <header className="empweek-head">
          <span className="gantt-chip" style={{ background: color }}>
            {chipLabel(employee)}
          </span>
          <strong>
            {employee.firstName} {employee.lastName}
          </strong>
          <span className="empweek-range">semaine du {frDayLabel(weekStart)}</span>
          <button type="button" className="empweek-close" onClick={onClose} aria-label="Fermer">
            ×
          </button>
        </header>
        <div className="empweek-nav">
          <button type="button" onClick={() => setWeekStart(shiftDay(weekStart, -7))}>
            ‹ semaine préc.
          </button>
          <button type="button" onClick={() => setWeekStart(shiftDay(weekStart, 7))}>
            semaine suiv. ›
          </button>
        </div>
        {err && <p className="auth-err">Erreur : {err}</p>}
        {!loaded && !err && <p className="staff-empty">Chargement…</p>}
        {loaded && (
          <ul className="empweek-days">
            {days.map((key) => {
              const presence = presenceByDay.get(key);
              const clocked = shiftSummary(clockByDay.get(key) ?? []);
              return (
                <li key={key} className={`empweek-day${key === today ? ' is-today' : ''}`}>
                  <span className="empweek-daylabel">{fmtShortDayLabel(key)}</span>
                  <EmpWeekPresence presence={presence} />
                  <span className={`empweek-clock${clocked ? '' : ' is-empty'}`}>
                    {key <= today ? clocked || '—' : ''}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

/** The Présence-matrix cell, standalone: full-red Absent, else the three filled/empty thirds, else « — ». */
function EmpWeekPresence({ presence }: { presence: DayPresence | undefined }) {
  if (!presence) return <span className="empweek-declared is-empty">non déclaré</span>;
  if (presence.absent) {
    return (
      <span className="empweek-declared empweek-absent" title="Absent·e">
        ✕ absent·e
      </span>
    );
  }
  const slots: PresenceSlot[] = [];
  if (presence.matin) slots.push('matin');
  if (presence.apresMidi) slots.push('apresMidi');
  if (presence.soir) slots.push('soir');
  return (
    <span className="empweek-declared" title={slotsLabel(slots)}>
      <span className="pmx-slots">
        <i className={`pmx-seg${presence.matin ? ' on' : ''}`} />
        <i className={`pmx-seg${presence.apresMidi ? ' on' : ''}`} />
        <i className={`pmx-seg${presence.soir ? ' on' : ''}`} />
      </span>
    </span>
  );
}
